import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { applyBrand, removeBrand, BRAND_REGISTRY } from '@/lib/brand-theme';

type Theme = 'dark' | 'light';
type Density = 'compact' | 'cozy';

interface ThemeState {
  theme: Theme;
  density: Density;
  brand: string | null;
  setTheme: (theme: Theme) => void;
  setDensity: (density: Density) => void;
  setBrand: (brand: string | null) => void;
  toggleTheme: () => void;
  toggleDensity: () => void;
}

function applyThemeToDOM(theme: Theme, density: Density, brand: string | null) {
  const html = document.documentElement;
  html.setAttribute('data-theme', theme);
  html.setAttribute('data-density', density);
  if (brand && BRAND_REGISTRY[brand]) {
    applyBrand(brand);
  } else {
    removeBrand();
  }
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      theme: 'dark',
      density: 'compact',
      brand: null,
      setTheme: (theme) => {
        set({ theme });
        applyThemeToDOM(theme, get().density, get().brand);
      },
      setDensity: (density) => {
        set({ density });
        applyThemeToDOM(get().theme, density, get().brand);
      },
      setBrand: (brand) => {
        set({ brand });
        applyThemeToDOM(get().theme, get().density, brand);
      },
      toggleTheme: () => {
        const next = get().theme === 'dark' ? 'light' : 'dark';
        set({ theme: next });
        applyThemeToDOM(next, get().density, get().brand);
      },
      toggleDensity: () => {
        const next = get().density === 'compact' ? 'cozy' : 'compact';
        set({ density: next });
        applyThemeToDOM(get().theme, next, get().brand);
      },
    }),
    {
      name: 'solidafy-theme-store',
    },
  ),
);

// Initialize theme from storage at module import time (prevents flash)
function initThemeFromStorage() {
  try {
    const stored = localStorage.getItem('solidafy-theme-store');
    if (stored) {
      const parsed = JSON.parse(stored) as { state?: { theme?: string; density?: string; brand?: string } } | null;
      const rawTheme = parsed?.state?.theme;
      const rawDensity = parsed?.state?.density;
      const rawBrand = parsed?.state?.brand;
      const theme: Theme = rawTheme === 'light' ? 'light' : 'dark';
      const density: Density = rawDensity === 'cozy' ? 'cozy' : 'compact';
      const brand: string | null = typeof rawBrand === 'string' ? rawBrand : null;
      applyThemeToDOM(theme, density, brand);
    } else {
      applyThemeToDOM('dark', 'compact', null);
    }
  } catch {
    applyThemeToDOM('dark', 'compact', null);
  }
}

initThemeFromStorage();
